import { getReorderPlacementFromElements } from '../interactions/reorder-list.ts';
import * as pipelineView from './pipeline-view.ts';
import type {
  LutReorderDragState,
  StepReorderDragState,
} from './pipeline-view.ts';

export interface StepDropPlacement {
  stepId: string | null;
  after: boolean;
}

export interface LutDropPlacement {
  lutId: string | null;
  after: boolean;
}

export interface PipelineDropIndicatorController {
  clearStepDropIndicators: () => void;
  updateStepDropIndicators: () => void;
  getStepDropPlacement: (clientY: number) => StepDropPlacement;
  clearLutDropIndicators: () => void;
  updateLutDropIndicators: () => void;
  getLutDropPlacement: (clientX: number) => LutDropPlacement;
}

export interface PipelineDropIndicatorControllerOptions {
  getStepListEl: () => HTMLElement;
  lutStripListEl: HTMLElement;
  parseStepId: (value: string | undefined) => string | null;
  getStepReorderDragState: () => StepReorderDragState | null;
  getLutReorderDragState: () => LutReorderDragState | null;
}

function isHTMLElement(value: unknown): value is HTMLElement {
  return value instanceof HTMLElement;
}

function ensureFunction(value: unknown, label: string): void {
  if (typeof value !== 'function') {
    throw new Error(`${label} が不正です。`);
  }
}

function ensureFiniteNumber(value: unknown, label: string): void {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} は有限の数値で指定してください。`);
  }
}

function ensureOptions(value: unknown): asserts value is PipelineDropIndicatorControllerOptions {
  if (!value || typeof value !== 'object') {
    throw new Error('PipelineDropIndicatorController の options が不正です。');
  }

  const options = value as Partial<PipelineDropIndicatorControllerOptions>;
  ensureFunction(options.getStepListEl, 'PipelineDropIndicatorController: getStepListEl');
  if (!isHTMLElement(options.lutStripListEl)) {
    throw new Error('PipelineDropIndicatorController: lutStripListEl が不正です。');
  }

  ensureFunction(options.parseStepId, 'PipelineDropIndicatorController: parseStepId');
  ensureFunction(options.getStepReorderDragState, 'PipelineDropIndicatorController: getStepReorderDragState');
  ensureFunction(options.getLutReorderDragState, 'PipelineDropIndicatorController: getLutReorderDragState');
}

export function createPipelineDropIndicatorController(
  options: PipelineDropIndicatorControllerOptions,
): PipelineDropIndicatorController {
  ensureOptions(options);
  const resolveStepListEl = (): HTMLElement => {
    const stepListEl = options.getStepListEl();
    if (!(stepListEl instanceof HTMLElement)) {
      throw new Error('PipelineDropIndicatorController: resolved stepListEl が不正です。');
    }
    return stepListEl;
  };

  const clearStepDropIndicators = (): void => {
    pipelineView.clearStepDropIndicators(resolveStepListEl());
  };

  const updateStepDropIndicators = (): void => {
    pipelineView.updateStepDropIndicators(resolveStepListEl(), options.getStepReorderDragState());
  };

  const getStepDropPlacement = (clientY: number): StepDropPlacement => {
    ensureFiniteNumber(clientY, 'Step ドロップ位置(clientY)');

    const stepReorderDragState = options.getStepReorderDragState();
    const stepListEl = resolveStepListEl();
    const placement = getReorderPlacementFromElements({
      elements: Array.from(stepListEl.querySelectorAll<HTMLElement>('[data-step-item="true"]')),
      getElementItemId: element => options.parseStepId(element.dataset.stepId),
      excludeId: stepReorderDragState?.stepId ?? null,
      axis: 'vertical',
      pointerCoord: clientY,
    });
    return { stepId: placement.targetId, after: placement.after };
  };

  const clearLutDropIndicators = (): void => {
    pipelineView.clearLutDropIndicators(options.lutStripListEl);
  };

  const updateLutDropIndicators = (): void => {
    pipelineView.updateLutDropIndicators(options.lutStripListEl, options.getLutReorderDragState());
  };

  const getLutDropPlacement = (clientX: number): LutDropPlacement => {
    ensureFiniteNumber(clientX, 'LUT ドロップ位置(clientX)');

    const lutReorderDragState = options.getLutReorderDragState();
    const placement = getReorderPlacementFromElements({
      elements: Array.from(options.lutStripListEl.querySelectorAll<HTMLElement>('[data-lut-item="true"]')),
      getElementItemId: element => element.dataset.lutId ?? null,
      excludeId: lutReorderDragState?.lutId ?? null,
      axis: 'horizontal',
      pointerCoord: clientX,
    });
    return { lutId: placement.targetId, after: placement.after };
  };

  return {
    clearStepDropIndicators,
    updateStepDropIndicators,
    getStepDropPlacement,
    clearLutDropIndicators,
    updateLutDropIndicators,
    getLutDropPlacement,
  };
}
